import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {DeviceEnrollmentSession} from '../shared/device-enrollment.models';

/**
 * Thin HTTP client for the monolith's register-enrolment API (`/device/enrollment`), reached through the
 * gateway under the SPA's single `BASE_URL` origin so the auth interceptor attaches the context token.
 *
 * ### Two things about `issue` that are not obvious from the signature
 *
 * **It is not idempotent, and that is the point.** Opening a session supersedes every session still
 * outstanding for the shard, so only the one now on screen works. Calling it twice in a row is exactly what
 * the *Neuen Code anzeigen* button — and the panel's silent renewal as the backstop approaches — does.
 *
 * **Its response carries the secret the codes are computed from.** The server keeps it encrypted; the browser
 * needs it in the clear to rotate the code. So the result is passed straight to the panel that renders it and
 * is never written anywhere — not to a store, not to `localStorage` (which `CookieService` traps anyway), and
 * not into a log line.
 *
 * ### There is no account call here any more (#279)
 *
 * A code binds to a company + shard and names no account, so the former `GET`/`POST /device/enrollment/accounts`
 * (the picker's listing and the "create a register account" action) are retired with the anchor they served.
 */
@Injectable({providedIn: 'root'})
export class DeviceEnrollmentHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.Device_Enrollment;
  }

  /**
   * Opens a fresh enrolment session for a shard, retiring whatever was outstanding there.
   *
   * @param shardId the shard the code is for. Omitted, the server uses the caller's own shard — which is what
   *                the screen does, because the session already names the Standort the administrator is on.
   */
  issue(shardId?: string | null): Observable<DeviceEnrollmentSession> {
    return this.http.post<DeviceEnrollmentSession>(this.base, {shardId: shardId ?? null});
  }
}
